<template>
  <div>
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <div
      v-if="authMessageText"
      class="container-fluid m-3 mb-4"
    >
      <p
        class="bb-message mt-3 p-2"
        style="white-space: pre-line"
      >
        {{ authMessageText }}
      </p>
    </div>
    <div
      v-if="noArenas"
      class="container-fluid m-3 mb-4"
    >
      <p class="bb-no-records mt-3 p-2">No campaigns created</p>
    </div>
    <div class="bb-arena-listing">
      <!-- loop thru data - call card component -->
      <div
        v-for="arena in arenas"
        :key="arena.id"
      >
        <CampaignCardComponent :arena="arena" />
      </div>
    </div>
  </div>
</template>

<script>
//=============================================================
// script
//--------
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CampaignCardComponent from '@/components/CampaignCard.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import createSocketConnection from '../helpers/createSocketConnection'
import formatHiddenRollValue from '../helpers/formatHiddenRollValue'
import handleError from '../helpers/handleError'
import store from '../store/index'

export default {
  name: 'campaignsView',
  components: {
    CampaignCardComponent,
    ConfirmDialog,
  },
  //=============================================================
  // data
  //------
  data() {
    return {
      arenas: [],
      connections: [],
      connection_resolvers: [],
      refreshIntervalId: null,
      noArenas: false,
      message: '',
      config: null,
    }
  },
  computed: {
    ...mapGetters('auth', {
      getMessage: 'getMessage',
      getUserProfile: 'getUserProfile',
    }),
    authMessageText() {
      if (!this.getMessage) {
        return ''
      }

      if (typeof this.getMessage === 'string') {
        return this.getMessage
      }

      return this.getMessage.text || ''
    },
  },
  //=============================================================
  // methods
  // --------
  methods: {
    ...mapActions('combat', {
      loadCampaign: 'loadCampaign',
    }),
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
    }),
    setView(view) {
      this.setCurrentView(view)
    },
    startArenaRefreshPolling() {
      if (this.refreshIntervalId) {
        return
      }

      this.refreshIntervalId = window.setInterval(() => {
        this.refreshVisibleArenas()
      }, 5000)
    },
    async refreshVisibleArenas() {
      if (document.hidden || this.arenas.length === 0) {
        return
      }

      await Promise.allSettled(
        this.arenas.map((arena) => this.loadCampaignHandler(arena.slug)),
      )
    },
    async showHiddenRollPopup(message = {}) {
      const currentUser = this.getUserProfile || {}
      const currentUserIds = [currentUser?.id, currentUser?.email]
        .filter(
          (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value))

      if (!currentUserIds.includes(String(message?.dm_id))) {
        return false
      }

      await this.$refs.confirmDialog?.show?.({
        title: 'Hidden Roll to DM',
        message: `from ${message?.player_name || 'Player'}`,
        subject: formatHiddenRollValue(message?.roll),
        subjectReadonly: true,
        okButton: 'OK',
      })

      return true
    },
    isCurrentUserHiddenRollRecipient(message = {}) {
      const currentUser = this.getUserProfile || {}
      const currentUserIds = [currentUser?.id, currentUser?.email]
        .filter(
          (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value))

      return currentUserIds.includes(String(message?.dm_id))
    },
    acknowledgeHiddenRollDelivery(connection, message = {}) {
      if (
        !message?.request_id ||
        !connection ||
        connection.readyState !== WebSocket.OPEN
      ) {
        return
      }

      connection.send(
        JSON.stringify({
          message: {
            type: 'hidden_roll_ack',
            request_id: message.request_id,
          },
        }),
      )
    },
    canSubscribeToArena(arena = {}) {
      const currentUserName = this.getUserProfile?.user_name
      const currentUserEmail = this.getUserProfile?.email
      const dmUserName = arena?.dm?.user_name
      const players = Array.isArray(arena?.players) ? arena.players : []

      if (currentUserName && dmUserName && dmUserName === currentUserName) {
        return true
      }

      return players.some(
        (player) =>
          (currentUserName && player?.user_name === currentUserName) ||
          (currentUserEmail && player?.email === currentUserEmail),
      )
    },
    //====================================================
    // list campaigns handler
    //---------------------
    async listArenasHandler() {
      //--------------
      // access store
      //--------------
      const this_store = store
      let endpoint = 'api/v1/campaigns/list/'
      this.loadingArenas = true
      //----------------
      // get all campaigns
      //----------------
      await this_store
        .dispatch('combat/listArenas', endpoint)
        //------------
        // got arenas
        //------------
        .then((arenas) => {
          if (arenas.length == 0) {
            this.noArenas = true
          } else {
            this.noArenas = false
            //------------------------
            // store arenas in one go
            //------------------------
            this.arenas.push(...arenas)
            //-----------------------------------------
            // create socket connection for each arena
            //-----------------------------------------
            this.arenas.forEach((arena) => {
              if (this.canSubscribeToArena(arena)) {
                this.createSocketConnectionHandler(arena.slug)
              }
            })
            this.startArenaRefreshPolling()
          }
        })
        .catch((error) => {
          handleError(error, 14)
        })
    },
    //========================================================
    // load arena handler
    //--------------------
    async loadCampaignHandler(arena_slug) {
      // console.log('--------------------')
      // console.log('IN loadCampaignHandler slug = ' + arena_slug)

      this.loadCampaign(arena_slug)
        .then((updated_arena) => {
          //------------------------------
          // find updated arena array idx
          //------------------------------
          let idx = this.arenas.findIndex((a) => a.slug == arena_slug)
          //--------------------------------
          // replace updated arena in array
          //--------------------------------
          this.arenas.splice(idx, 1, updated_arena)
        })
        .catch((err) => {
          console.log('Skipping campaign refresh for slug = ' + arena_slug, err)
        })
    },
    //====================================================
    // create socket connection handler
    //----------------------------------
    createSocketConnectionHandler(arena_slug) {
      if (
        this.connections.some(
          (connection) => connection.arena_slug === arena_slug,
        )
      ) {
        return
      }

      // console.log("IN createSocketConnectionHandler = " + arena_slug);
      //-------------------
      // create connection
      //-------------------
      let this_connection = createSocketConnection('arena', arena_slug)
      this_connection.arena_slug = arena_slug
      var self = this
      //------------------
      // message received
      //------------------
      this_connection.onmessage = function (event) {
        const data = JSON.parse(event.data)
        const message = data.message
        console.log('received message = ', message)

        if (message?.type === 'hidden_roll_delivery') {
          if (self.isCurrentUserHiddenRollRecipient(message)) {
            self.acknowledgeHiddenRollDelivery(this_connection, message)
          }
          void self.showHiddenRollPopup(message)
          return
        }
        //----------------------
        // reload updated campaign
        //----------------------
        self.loadCampaignHandler(arena_slug)
      }
      //---------------------
      // connection listener
      //---------------------
      this_connection.addEventListener('open', () => {
        this.connection_resolvers.forEach((r) => r.resolve())
      })
      //-------------------
      // store connections
      //-------------------
      this.connections.push(this_connection)
    },
  },
  //=============================================================
  // created
  //---------
  mounted() {
    //----------
    // set view
    //----------
    this.setView('campaigns')
    //----------------------
    // list existing campaigns
    //----------------------
    this.listArenasHandler()
  },
  unmounted() {
    if (this.refreshIntervalId) {
      window.clearInterval(this.refreshIntervalId)
      this.refreshIntervalId = null
    }

    this.connections.forEach((connection) => {
      if (connection && connection.readyState === WebSocket.OPEN) {
        connection.close(1000)
      }
    })
    this.connections = []
  },
}
</script>
